import type { components } from '@feedfm/clips-api-client';
import {
  Alert,
  Box,
  Button,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDemoAppContext } from '../../pages/demo/DemoAppContext';
import './AppStart.css';

const AppStart = () => {
  const {
    clientId: contextClientId,
    client,
    setClientid: setContextClientId,
  } = useDemoAppContext();
  const [clientId, setClientId] = useState(contextClientId);
  const [response, setResponse] = useState<
    components['schemas']['AppStartResponse'] | null
  >(null);
  const [error, setError] = useState<string | null>(null);

  // This fires when clientId changes in the context. setClientId will also try to update clientId
  // in the context. This is not an infinite loop because the value is identical and React ignores
  // it.
  useEffect(() => {
    setClientId(contextClientId);
  }, [contextClientId]);

  // Get browser info
  const platform = /iPhone|iPad|iPod/.test(navigator.userAgent) ? 'iOS' : 'Web';
  const timestamp = new Date().toISOString();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResponse(null);

    const response = await client.POST('/app-start', {
      body: {
        clientId,
        platform,
      },
    });

    if (response.error) {
      setError(response.error ?? 'Failed to start app');
    }

    if (response.data) {
      setResponse(response.data);
      if (response.data.success) {
        setContextClientId(clientId);
      }
    }
  };

  return (
    <Paper elevation={3} className="app-start-paper">
      <Typography variant="h5" component="h2" gutterBottom>
        App Start (POST /app-start)
      </Typography>
      <Typography variant="body2" color="textSecondary">
        This endpoint should be called when the app is started.
      </Typography>

      <form onSubmit={handleSubmit}>
        <Box className="app-start-container">
          <TextField
            fullWidth
            label="Client ID"
            variant="outlined"
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value);
            }}
            className="app-start-textfield"
            required
          />

          <Box className="app-start-info">
            <Typography variant="body2" color="textSecondary">
              Platform: {platform}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Timestamp: {timestamp}
            </Typography>
          </Box>

          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={client.getToken() === ''}
            className="app-start-button"
          >
            Start App
          </Button>
        </Box>
      </form>

      {error && (
        <Alert severity="error" className="app-start-error">
          {error}
        </Alert>
      )}

      {response && (
        <Box className="app-start-response">
          <Typography variant="h6" gutterBottom>
            Response:
          </Typography>
          <Paper variant="outlined" className="app-start-response-paper">
            <pre className="app-start-response-pre">
              {JSON.stringify(response, null, 2)}
            </pre>
          </Paper>
        </Box>
      )}
    </Paper>
  );
};

export default AppStart;
